import { useContentAttach } from '@components/App/Modal/BlockModal'
import { parseEditorContent } from '@components/ArtBoard/Comments/components/Comment/components/Content'
import { buildMention } from '@components/ArtBoard/Comments/components/CommentsForm'
import { LiteEditor, LiteView } from '@octopus-do/octopus-editor'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import type React from 'react'
import { useState } from 'react'
import { useSelector } from 'react-redux'

import { useArtBoardDisabled } from '@/hooks/selectors/useArtBoardDisabled'
import type { Recipient } from '@/types'
import { clsJoin, isSubmitHotkey } from '@/utils'
import css from './styles.module.css'

interface Props {
  value: string
  readonly: boolean
  onSubmit: (value: string) => void
  onCancel: () => void
}

export const CommentContentEditor: React.FC<Props> = ({
  readonly,
  value,
  onSubmit,
  onCancel,
}) => {
  const [state, setState] = useState(() => parseEditorContent(value))

  const { onUpload } = useContentAttach()

  const members: Recipient[] = useSelector(
    migrationProjectSelector.getProjectRecipients
  )

  const mentionData = members.map(buildMention)

  // the artboard went off with the comment open for editing: the text is
  // kept, but takes no typing and is not saved until the artboard is back
  const artboardDisabled = useArtBoardDisabled()

  const onSaveClick = () => {
    onSubmit(JSON.stringify(state))
  }

  // the editor has no onKeyDown of its own, the keydown bubbles up to here
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (!isSubmitHotkey(event) || artboardDisabled) {
      return
    }

    event.preventDefault()
    onSaveClick()
  }

  const handleCancel = () => {
    setState(parseEditorContent(value))
    onCancel()
  }

  if (readonly) {
    return (
      <div className={css.comment_editor}>
        <LiteView value={state} />
      </div>
    )
  }

  return (
    <div className={css.comment_editor} onKeyDown={onKeyDown}>
      <LiteEditor
        autofocus
        mentions={mentionData}
        onChange={setState}
        onFilesUpload={onUpload}
        value={state}
        readonly={artboardDisabled}
      />
      <div className={css.buttons}>
        <button
          onClick={handleCancel}
          type="button"
          className={clsJoin(css.button, css.cancel)}
        >
          Cancel
        </button>
        <button
          onClick={onSaveClick}
          className={clsJoin(css.button, css.save)}
          type="button"
          disabled={artboardDisabled}
        >
          Save
        </button>
      </div>
    </div>
  )
}
